import { fNumber, fShortenNumber } from 'src/utils/format-number';
import { DashboardCard } from './DashboardCard';
import { CardGrid } from './CardGrid';
import {
  PredictionActiveWalletsSummary,
  PredictionDashboardFilter,
  PredictionTotalsSummary,
  PredictionUserGrowthSummary,
  predictionDashboardResources,
} from '../dataProvider';

const icon = {
  profit: 'flowbite:chart-mixed-dollar-solid',
  user: 'uil:user',
  deposit: 'flowbite:arrow-down-to-bracket-solid',
  withdraw: 'mdi:bank-transfer-out',
  revenue: 'heroicons-outline:currency-dollar',
} as const;

const unitKey = {
  currency: 'module.dashBoard.unitCurrency',
  user: 'module.dashBoard.unitUsers',
} as const;

type PredictionListCardsProps = {
  filter: PredictionDashboardFilter;
  depositType: string;
  withdrawType: string;
};

type TransferStatsRecord = {
  amount?: string | number;
};

type TransferStatsFilter = PredictionDashboardFilter & {
  type: string;
};

export const PredictionListCards = ({
  filter,
  depositType,
  withdrawType,
}: PredictionListCardsProps) => (
  <CardGrid>
    <DashboardCard<PredictionUserGrowthSummary, PredictionDashboardFilter>
      resource={predictionDashboardResources.userGrowth}
      filter={filter}
      title="module.dashBoard.newUsers"
      tooltip="module.dashBoard.newUsersTooltip"
      icon={icon.user}
      unitKey={unitKey.user}
      color="error"
      initialData={{ id: 'summary' }}
      getValues={(data) => [Number(data?.totalNewUsers ?? 0)]}
    />
    <DashboardCard<TransferStatsRecord, TransferStatsFilter>
      resource="dashboard/transfer-stats"
      filter={{ ...filter, type: depositType }}
      title="module.dashBoard.deposit"
      tooltip="module.dashBoard.depositTooltip"
      icon={icon.deposit}
      unitKey={unitKey.currency}
      color="success"
      getValues={(data) => [Number(data?.amount ?? 0)]}
    />
    <DashboardCard<PredictionTotalsSummary, PredictionDashboardFilter>
      resource={predictionDashboardResources.totals}
      filter={filter}
      title="module.dashBoard.tradingStatsNotional"
      tooltip="module.dashBoard.tradingStatsTooltip"
      icon={icon.profit}
      unitKey={unitKey.currency}
      color="info"
      initialData={{ id: 'summary' }}
      getValues={(data) => [Number(data?.notional ?? 0)]}
      getTotalLabel={(data) => {
        const value = Number(data?.notional ?? 0);

        return {
          text: `USD ${fShortenNumber(value)}`,
          tooltip: `USD ${fNumber(value, { maximumFractionDigits: 2 })}`,
        };
      }}
    />
    <DashboardCard<PredictionActiveWalletsSummary, PredictionDashboardFilter>
      resource={predictionDashboardResources.activeWallets}
      filter={filter}
      title="module.dashBoard.activeTraders"
      tooltip="module.dashBoard.activeTradersTooltip"
      icon={icon.user}
      unitKey={unitKey.user}
      color="info"
      initialData={{ id: 'summary' }}
      getValues={(data) => [Number(data?.total ?? 0)]}
    />
    <DashboardCard<TransferStatsRecord, TransferStatsFilter>
      resource="dashboard/transfer-stats"
      filter={{ ...filter, type: withdrawType }}
      title="module.dashBoard.withdraw"
      tooltip="module.dashBoard.withdrawTooltip"
      icon={icon.withdraw}
      unitKey={unitKey.currency}
      color="error"
      getValues={(data) => [Number(data?.amount ?? 0)]}
    />
    <DashboardCard<PredictionTotalsSummary, PredictionDashboardFilter>
      resource={predictionDashboardResources.totals}
      filter={filter}
      title="module.dashBoard.tradingStatsUsdc"
      tooltip="module.dashBoard.tradingStatsTooltip"
      icon={icon.profit}
      unitKey={unitKey.currency}
      color="warning"
      initialData={{ id: 'summary' }}
      getValues={(data) => [Number(data?.usdc ?? 0)]}
      getTotalLabel={(data) => {
        const value = Number(data?.usdc ?? 0);

        return {
          text: `USDC ${fShortenNumber(value)}`,
          tooltip: `USDC ${fNumber(value, { maximumFractionDigits: 2 })}`,
        };
      }}
    />
    <DashboardCard<PredictionTotalsSummary, PredictionDashboardFilter>
      resource={predictionDashboardResources.revenue}
      filter={filter}
      title="module.dashBoard.revenue"
      tooltip="module.dashBoard.revenueTooltip"
      icon={icon.revenue}
      unitKey={unitKey.currency}
      color="warning"
      initialData={{ id: 'summary' }}
      getValues={(data) => {
        const items = data?.items ?? [];
        return items.map((item: any) => Number(item?.builderFee ?? 0));
      }}
      getTotalLabel={(data) => {
        const items = data?.items ?? [];
        const totalBuilderFee = items.reduce(
          (sum: any, item: any) => sum + Number(item?.builderFee ?? 0),
          0
        );

        return {
          text: `USD ${fShortenNumber(totalBuilderFee)}`,
          tooltip: `USD ${fNumber(totalBuilderFee, { maximumFractionDigits: 2 })}`,
        };
      }}
    />
  </CardGrid>
);
